import React, { useState, useEffect, FC } from 'react';
import { useAppDispatch } from '../hooks';
import { useNavigate } from 'react-router-dom';
import { addNewHerd } from '../reducers/herdReducer';
import { Formik, Form } from 'formik';
import { TextInput } from './FormikMuiFields';
import { notify } from '../reducers/notificationReducer';
import AlertMessage from './AlertMessage';
import * as yup from 'yup';
import getErrorMsg from '../utils/getErrorMsg';
import posthog from '../utils/posthog';
import TopicPicker from './TopicPicker';

import { Button, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import RefreshIcon from '@mui/icons-material/Refresh';

const validationSchema = yup.object({
  herdName: yup
    .string()
    .required('Required')
    .max(20, 'Must be at most 20 characters')
    .min(3, 'Must be at least 3 characters')
    .matches(
      /^[a-zA-Z0-9-_]*$/,
      'Only alphanumeric characters allowed, no spaces/symbols'
    ),
  description: yup
    .string()
    .required('Required')
    .max(100, 'Must be at most 100 characters')
    .min(3, 'Must be at least 3 characters'),
});

const generateRandomImage = (artworkTokens: any): string => {
  const size = 200;

  // Utilities
  const rand = (min: number, max: number) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  const randFloat = (min: number, max: number) =>
    Math.random() * (max - min) + min;

  // Muted / washed-out dark palettes
  const palettes = artworkTokens.palettes;

  const palette = palettes[rand(0, palettes.length - 1)];
  const pick = () => palette[rand(0, palette.length - 1)];

  const bg1 = pick();
  const bg2 = pick();

  const shapeCount = rand(6, 14);
  let shapes = '';

  for (let i = 0; i < shapeCount; i++) {
    const type = rand(0, 2); // circle | blob | line
    const color = pick();
    const opacity = randFloat(0.18, 0.55);
    const x = rand(-30, size + 30);
    const y = rand(-30, size + 30);

    if (type === 0) {
      // Circle
      shapes += `
        <circle
          cx="${x}"
          cy="${y}"
          r="${rand(12, 52)}"
          fill="${color}"
          opacity="${opacity}"
        />
      `;
    } else if (type === 1) {
      // Organic blob
      const r = rand(25, 65);
      shapes += `
        <path
          d="
            M ${x} ${y}
            C ${x + r} ${y - r},
              ${x + r * 1.4} ${y + r},
              ${x} ${y + r * 1.4}
            C ${x - r} ${y + r},
              ${x - r * 1.4} ${y - r},
              ${x} ${y}
          "
          fill="${color}"
          opacity="${opacity}"
        />
      `;
    } else {
      // Line
      shapes += `
        <line
          x1="${x}"
          y1="${y}"
          x2="${x + rand(-90, 90)}"
          y2="${y + rand(-90, 90)}"
          stroke="${color}"
          stroke-width="${rand(2, 6)}"
          opacity="${opacity}"
          stroke-linecap="round"
        />
      `;
    }
  }

  const svg = `
    <svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${bg1}" />
          <stop offset="100%" stop-color="${bg2}" />
        </linearGradient>
      </defs>

      <rect width="${size}" height="${size}" rx="${artworkTokens.cornerRadius}" fill="url(#bg)" />

      ${shapes}

      <!-- subtle depth -->
      <rect width="${size}" height="${size}" fill="${artworkTokens.overlay}" />
    </svg>
  `;

  return 'data:image/svg+xml;base64,' + btoa(svg);
};

interface HerdFormProps {
  // When provided, called with the created herd instead of navigating to
  // its page — lets embedding flows (e.g. the post form) keep their context.
  onSuccess?: (herd: { id: string; herdName: string }) => void;
}

const HerdForm: FC<HerdFormProps> = ({ onSuccess }) => {
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null>(null);
  const [initialImage, setInitialImage] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const formWrapperSx = {};
  const formSx = { display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingLeft: 2.5, paddingRight: 2.5, paddingBottom: 2.5 };
  const inputSx = { display: 'flex', alignItems: 'flex-end' };
  const descInputSx = { display: 'flex', alignItems: 'flex-end', marginTop: 'var(--space-stack-large)' };
  const submitButtonSx = { marginTop: 'var(--space-stack-xlarge)', marginBottom: 'var(--space-stack-tight)' };

  useEffect(() => {
    const randomImage = generateRandomImage(theme.tokens.artwork);
    setInitialImage(randomImage);
    setImagePreview(randomImage);
  }, [theme.tokens.artwork]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>, setFieldValue: (field: string, value: any) => void) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFieldValue('herdImage', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRegenerateImage = (setFieldValue: (field: string, value: any) => void) => {
    const randomImage = generateRandomImage(theme.tokens.artwork);
    setImagePreview(randomImage);
    setFieldValue('herdImage', randomImage);
  };

  const handleCreateSub = async (values: any, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    try {
      setSubmitting(true);
      const createdSub = await dispatch(addNewHerd(values));
      setSubmitting(false);
      posthog.capture('herd_created', { herd_name: values.herdName });
      dispatch(
        notify(`New herd created: ${values.herdName}`, 'success')
      );
      if (onSuccess) {
        onSuccess(createdSub);
      } else {
        navigate(`/herd/${values.herdName}`);
      }
    } catch (err) {
      setSubmitting(false);
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };

  return (
    <Box sx={formWrapperSx}>
      <Formik
        validateOnChange={true}
        initialValues={{ herdName: '', description: '', herdImage: initialImage || '', topics: [] as string[] }}
        onSubmit={handleCreateSub}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <Form>
            <Box sx={formSx}>
              <Box sx={inputSx}>
                <TextInput name="herdName" type="text" placeholder="Enter name" label="Herd Name" required fullWidth />
              </Box>
              <Box sx={descInputSx}>
                <TextInput name="description" type="text" placeholder="Enter description" label="Description" required fullWidth variant="outlined" multiline minRows={2} maxRows={Infinity} />
              </Box>
              <Box sx={{ marginTop: 'var(--space-stack-large)' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Topics
                </Typography>
                <TopicPicker
                  value={values.topics}
                  onChange={(topics) => setFieldValue('topics', topics)}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', marginTop: 'var(--space-stack-large)', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ImageIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">Herd Image</Typography>
                </Box>
                {imagePreview && (
                  <Box sx={{ mt: 1, mb: 1, position: 'relative' }}>
                    <img src={imagePreview as string} alt="Preview" style={{ maxWidth: 'var(--layout-image-preview)', maxHeight: 'var(--layout-image-preview)', borderRadius: 'var(--radius-image)' }} />
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<RefreshIcon />}
                      sx={{ mt: 0.5, mr: 0.5 }}
                      onClick={() => handleRegenerateImage(setFieldValue)}
                    >
                      Generate
                    </Button>
                  </Box>
                )}
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
                  Upload a custom image or use the generated one:
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageSelect(e, setFieldValue)}
                  style={{ marginBottom: theme.tokens.spacing.stackTight }}
                />
              </Box>
              <Button type="submit" variant="contained" size="large" sx={submitButtonSx} disabled={isSubmitting} startIcon={<AddIcon />}>{isSubmitting ? 'Creating' : 'Create Herd'}</Button>
            </Box>
          </Form>
        )}
      </Formik>
      <AlertMessage
        error={error}
        severity="error"
        clearError={() => setError(null)}
      />
    </Box>
  );
};

export default HerdForm;
